import React, { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import { Stack } from '@mui/system';
import { Typography } from '@mui/material';
import { getToken } from '../../auth/auth.js';

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Creates initials from a display name.
 * @param {string} name The user's full display name.
 * @returns {string} The initials.
 */
function getInitials(name = '') {
  if (!name) return '?'
  const parts = name.split(' ').filter(part => !part.startsWith('['))
  if (parts.length < 2) return ''
  const firstNameInitial = parts[0][0]
  const lastNameInitial = parts[parts.length - 1][0]
  return `${firstNameInitial}${lastNameInitial}`.toUpperCase()
}


// --- Main Component ---

/**
 * Displays a user's contact card with an avatar that falls back to initials.
 * Fetches data from Microsoft Graph API using a provided access token.
 * @param {{profileEmail: string, profileName: string, profileJobTitle: string, avatarLeftRight: string, avatarHeight: number, avatarWidth: number, hideTitle: boolean}} props
 */
function ProfileAvatarCard({ profileEmail, profileName, profileJobTitle, avatarLeftRight='left', avatarHeight=48, avatarWidth=48, hideTitle=false }) {
  const [userData, setUserData] = useState(null);
  const [avatarSrc, setAvatarSrc] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Reset state when the email prop changes
    setUserData(null);
    setAvatarSrc(null);
    setError(null);
    setIsLoading(true);

    const fetchGraphData = async () => {
      if (!profileEmail) {
        setError('No user email provided.');
        setIsLoading(false);
        return;
      }

      try {
        const accessToken = (await getToken()).accessToken;
        if (!accessToken) {
          throw new Error('Could not retrieve access token.');
        }

        const headers = { Authorization: `Bearer ${accessToken}` };

        // Query users collection by mail/UPN because /users/{email} fails for guest/external identities.
        const normalizedEmail = profileEmail.trim().toLowerCase();
        const safeEmail = normalizedEmail.replace(/'/g, "''");
        const filter = encodeURIComponent(`mail eq '${safeEmail}' or userPrincipalName eq '${safeEmail}'`);
        const select = encodeURIComponent('id,displayName,jobTitle,mail,department,userPrincipalName');
        const dataEndpoint = `https://graph.microsoft.com/v1.0/users?$filter=${filter}&$select=${select}&$top=1`;

        const dataResponse = await fetch(dataEndpoint, { headers });
        if (!dataResponse.ok) {
          throw new Error(`Failed to fetch user data: ${dataResponse.statusText}`);
        }

        const dataJson = await dataResponse.json();
        const fetchedUserData = Array.isArray(dataJson?.value) ? dataJson.value[0] : undefined;
        if (!fetchedUserData?.id) {
          throw new Error(`User not found in Microsoft Graph for ${profileEmail}`);
        }
        setUserData(fetchedUserData);

        // Fetch profile photo by stable Graph id; 404 is expected when no photo exists.
        const photoEndpoint = `https://graph.microsoft.com/v1.0/users/${fetchedUserData.id}/photo/$value`;
        const photoResponse = await fetch(photoEndpoint, { headers });
        if (photoResponse.ok) {
          const imageBlob = await photoResponse.blob();
          const imageDataUrl = await blobToDataUrl(imageBlob);
          setAvatarSrc(imageDataUrl);
        }

      } catch (err) {
        console.error(err);
        setError(err?.message || 'Could not load profile.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGraphData();
  }, [profileEmail]);

  if (isLoading) {
    return <div>Loading profile...</div>;
  }

  if (error) {
    return <div>{error}</div>
  }

  return (
    <span >
      <Stack direction={avatarLeftRight === 'left' ? 'row' : 'row-reverse'} sx={{display: 'inline-flex', alignItems: 'center', fontFamily: 'sans-serif'}} gap='16px'>
      <Avatar
        src={avatarSrc}
        sx={{
          width: avatarWidth,
          height: avatarHeight,
          fontSize: '1.0em',
          bgcolor: '#222d32', //stringToColor(userData?.displayName ?? profileName),
          border: '1px solid #fff'
        }}
      >
        {getInitials(userData?.displayName ?? profileName)}
      </Avatar>
      <Stack rowGap={.5} padding='1%'>
        <Typography variant="body" whiteSpace='nowrap'>
          {userData?.displayName ?? profileName}
        </Typography>
        { (!hideTitle) && (userData?.jobTitle || profileJobTitle) && 
          <Typography variant="caption" fontStyle='text.secondary'>
            { userData?.jobTitle ?? profileJobTitle }
          </Typography>
        }
      </Stack>
      </Stack>
    </span>
  );
}

export default ProfileAvatarCard;